import React, { useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import styles from "./videoControls.module.css";

interface VideoControlsProps {
  video?: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setVolume: (val: number) => void;
  setIsMuted: (val: boolean) => void;
  setCurrentTime: (time: number) => void;
  onVideoPlay?: () => void;
  onVideoPause?: () => void;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  video,
  videoRef,
  currentTime,
  duration,
  isPlaying,
  volume,
  isMuted,
  setIsPlaying,
  setVolume,
  setIsMuted,
  setCurrentTime,
  onVideoPlay,
  onVideoPause,
}) => {
  const togglePlay = useCallback(() => {
    if (video) {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
          if (onVideoPause) {
            console.log("Video paused");
            onVideoPause();
          }
        } else {
          videoRef.current
            .play()
            .then(() => {
              if (onVideoPlay) {
                console.log("Button Pressed | Video playing");
                onVideoPlay();
              }
            })
            .catch((err) => console.error("Error playing video:", err));
        }
      }
    } else {
      // Media mode: toggle play state and trigger callbacks accordingly.
      setIsPlaying((prev) => {
        const newPlaying = !prev;
        if (newPlaying && onVideoPlay) {
          console.log("Media mode: Button Pressed | triggering onVideoPlay");
          onVideoPlay();
        }
        if (!newPlaying && onVideoPause) {
          console.log("Media mode: Button Pressed | triggering onVideoPause");
          onVideoPause();
        }
        return newPlaying;
      });
    }
  }, [video, videoRef, isPlaying, onVideoPlay, onVideoPause, setIsPlaying]);

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    // Update video element if available
    if (video && videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    
    // Update video element if available
    if (video && videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - bounds.left) / bounds.width;
    const newTime = duration * percentage;
    setCurrentTime(newTime);
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className={styles.container}>
      {/* Playback Controls */}
      <div className={styles.playback}>
        <button 
          onClick={togglePlay} 
          className={styles.button}
          onMouseEnter={() => console.log('Button hovered')}
          onMouseDown={() => console.log('Button mouse down')}
          style={{ 
            // Ensure button is fully interactive
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        >
          {isPlaying ? <Pause className={styles.icon} /> : <Play className={styles.icon} />}
        </button>
        <div className={styles.timeText}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>

      {/* Volume Controls */}
      <div className={styles.volumeControls}>
        <button onClick={toggleMute} className={styles.button}>
          {isMuted ? <VolumeX className={styles.icon} /> : <Volume2 className={styles.icon} />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          className={styles.slider}
        />

        {/* Fullscreen Control */}
        <div className={styles.fullscreen}>
          <button
            onClick={() => videoRef.current?.requestFullscreen()}
            className={styles.button}
          >
            <Maximize2 className={styles.icon} />
          </button>
        </div>
      </div>

      {/* Timeline */}
      {/* <div className={styles.timeline} onClick={handleTimelineClick}>
        <div
          className={styles.timelineProgress}
          style={{ width: `${(currentTime / duration) * 100}%` }}
        />
      </div> */}
    </div>
  );
};

export default VideoControls;
